import { Checkbox, DateInput, Input, RichTextEditor, Select, TagSelect, Textarea } from '@orbind/ui'
import { aplicarMascara, evaluateFilter, layoutDaEscolha, mascaraDoCampo } from '@orbind/core'
import { CampoBotao, CampoImagem, CampoLink } from './campos-tipos.tsx'
import { uploadWorkItemImage } from './api.ts'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { api } from '../../lib/api.ts'
import type { CustomField, CustomFieldPlacement } from '../admin/custom-field-data.ts'
import { regraDoConfig, valoresParaCriterios } from '../admin/custom-field-rules.ts'
import { useWiMutation } from './api.ts'

/** Campos nativos usados nas condições das regras (fase 2). */
export type ItemParaRegras = {
  workItemTypeId: string | null
  workItemStatusId: string | null
  workItemPriorityId: string | null
  flowStepId: string | null
  companyAreaId: string | null
  companyId: string | null
}

/** Regra de visibilidade de um config (colocação ou grupo) bate? Sem regra = sim. */
export function visivelPorRegra(
  config: Record<string, unknown> | undefined,
  item: ItemParaRegras,
  valores: Record<string, unknown>,
): boolean {
  const r = regraDoConfig(config, 'visibilityRule')
  return !r || evaluateFilter(valoresParaCriterios(item, valores), r)
}

/** Mantém só os campos cuja regra de visibilidade da COLOCAÇÃO bate. */
export function filtrarVisiveis<T extends { placement: CustomFieldPlacement }>(
  lista: T[],
  item: ItemParaRegras,
  valores: Record<string, unknown>,
): T[] {
  return lista.filter(({ placement }) => visivelPorRegra(placement.config, item, valores))
}

/**
 * Compartilhado entre o render dos campos complementares na aba
 * (CamposComplementaresItem) e na barra de propriedades (CamposComplementaresProps).
 * Guarda o estado dos valores do item e desenha o input por tipo, gravando no
 * JSONB `customValues` via PATCH.
 */

export const rotuloCampo = (labels: Record<string, string>, fallback: string) =>
  labels['pt-BR'] || Object.values(labels)[0] || fallback

/** Estado dos valores + gravação (JSONB customValues) de um item. */
export function useCustomValues(workItemId: string, customValues: Record<string, unknown>) {
  const [valores, setValores] = useState<Record<string, unknown>>(customValues ?? {})
  useEffect(() => setValores(customValues ?? {}), [customValues])

  const salvar = useWiMutation(workItemId, async (novos: Record<string, unknown>) =>
    api['work-items'][':id'].$patch({ param: { id: workItemId }, query: {}, json: { customValues: novos } as never }),
  )
  const gravar = (key: string, valor: unknown) => {
    const novos = { ...valores, [key]: valor }
    setValores(novos)
    salvar.mutate(novos)
  }
  return { valores, setValores, gravar }
}

/**
 * Guarda a altura que o usuário deu a um campo de texto, por REGISTRO + campo
 * (pedido 2026-08-31).
 *
 * O texto longo e o parágrafo podem ser esticados pela alça; sem isso a altura
 * voltava ao padrão toda vez que o item era fechado e aberto de novo. Segue a
 * mesma ideia da Descrição (ver ResizableField): a medida fica no
 * localStorage, presa ao registro.
 *
 * O gatilho para gravar é o `style.height` inline — só existe depois que
 * alguém arrastou a alça (ou depois de restaurarmos), então mudanças de altura
 * causadas pelo conteúdo não viram uma medida "escolhida".
 */
function AlturaLembrada({ chave, seletor, children }: { chave: string | null; seletor: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const alvo = ref.current?.querySelector(seletor) as HTMLElement | null
    if (!alvo) return
    if (!chave) return
    const salvo = Number(window.localStorage.getItem(chave))
    alvo.style.height = Number.isFinite(salvo) && salvo > 0 ? `${salvo}px` : ''

    const guardar = () => {
      if (!alvo.style.height) return
      window.localStorage.setItem(chave, String(Math.round(alvo.getBoundingClientRect().height)))
    }
    // o fim do arrasto da alça é um mouseup sobre o próprio campo — é o sinal
    // mais confiável; o observer cobre o resto (teclado, zoom, toque)
    alvo.addEventListener('mouseup', guardar)
    let tid: number | undefined
    const ro = new ResizeObserver(() => {
      window.clearTimeout(tid)
      tid = window.setTimeout(guardar, 300)
    })
    ro.observe(alvo)
    return () => {
      alvo.removeEventListener('mouseup', guardar)
      ro.disconnect()
      window.clearTimeout(tid)
    }
  }, [chave, seletor])

  return (
    <div className="wi-cfalt" ref={ref}>
      {children}
    </div>
  )
}

export function CampoInput({
  f,
  valores,
  setValores,
  gravar,
  escopo,
}: {
  f: CustomField
  valores: Record<string, unknown>
  setValores: (fn: (s: Record<string, unknown>) => Record<string, unknown>) => void
  gravar: (key: string, valor: unknown) => void
  /** Registro dono dos valores — usado para lembrar a altura dos campos de texto. */
  escopo?: string
}) {
  const v = valores[f.key]
  /**
   * Escolha em lista, data e sim/não mudam de valor de uma vez só: atualiza o
   * estado local E manda gravar. Não dá para depender só do `gravar` — em tela
   * que salva no botão Salvar (Empresa/Pessoa) ele não persiste nada na hora,
   * e a escolha simplesmente não aparecia.
   */
  const aplicar = (valor: unknown) => {
    setValores((s) => ({ ...s, [f.key]: valor }))
    gravar(f.key, valor)
  }
  /** Onde a altura escolhida para este campo fica guardada. */
  const chaveAltura = escopo ? `wi-cf-h:${escopo}:${f.key}` : null
  switch (f.type) {
    case 'textarea':
      return (
        <AlturaLembrada chave={chaveAltura} seletor="textarea">
          <Textarea
            rows={2}
            value={(v as string) ?? ''}
            onChange={(e) => setValores((s) => ({ ...s, [f.key]: e.target.value }))}
            onBlur={(e) => gravar(f.key, e.target.value)}
          />
        </AlturaLembrada>
      )
    // parágrafo: o MESMO editor da descrição do item — mudou lá, muda aqui
    case 'richtext':
      return (
        <AlturaLembrada chave={chaveAltura} seletor=".wi-rtebox">
          {/* wi-rtebox--rz: ganha a alça de altura, como o texto longo já tem */}
          <div className="wi-rtebox wi-rtebox--rz">
            <RichTextEditor
              value={(v as string) ?? ''}
              onSave={(html) => aplicar(html)}
              placeholder="Sem conteúdo — clique duas vezes para editar."
              minHeight={120}
              onUploadImage={uploadWorkItemImage}
            />
          </div>
        </AlturaLembrada>
      )
    case 'link':
      return (
        <CampoLink
          value={(v as string) ?? ''}
          onChange={(texto) => setValores((s) => ({ ...s, [f.key]: texto }))}
          onCommit={(texto) => aplicar(texto)}
        />
      )
    case 'image':
      return <CampoImagem value={(v as string) ?? ''} rotulo={rotuloCampo(f.labels, f.key)} onChange={aplicar} />
    // botão não guarda valor: o endereço mora na configuração do campo
    case 'button':
      return (
        <CampoBotao
          template={typeof f.config.buttonUrl === 'string' ? f.config.buttonUrl : ''}
          rotulo={typeof f.config.buttonLabel === 'string' ? f.config.buttonLabel : ''}
          valores={valores}
        />
      )
    case 'number':
      return (
        <Input
          type="number"
          value={v === undefined || v === null ? '' : String(v)}
          onChange={(e) => setValores((s) => ({ ...s, [f.key]: e.target.value }))}
          onBlur={(e) => gravar(f.key, e.target.value === '' ? null : Number(e.target.value))}
        />
      )
    case 'date':
    case 'datetime':
      return (
        <DateInput
          value={typeof v === 'string' && v ? new Date(v) : null}
          onChange={(d) => aplicar(d ? d.toISOString() : null)}
        />
      )
    case 'boolean':
      return <Checkbox checked={v === true} onChange={(e) => aplicar(e.target.checked)} label="Sim" />
    /**
     * Escolha única: as MESMAS opções em dois layouts. Trocar lista ↔ tag mexe
     * só na aparência — as opções e o valor gravado continuam iguais.
     */
    case 'select': {
      const opcoes = f.config.options ?? []
      if (layoutDaEscolha(f.config) === 'tag') {
        const atual = typeof v === 'string' && v ? [v] : []
        return (
          <TagSelect
            value={atual}
            options={opcoes}
            emptyLabel="Nenhuma opção cadastrada"
            placeholder="Escolha…"
            // única: a nova escolha substitui a anterior
            onChange={(vals) => aplicar(vals.length ? (vals[vals.length - 1] ?? null) : null)}
          />
        )
      }
      return (
        <Select
          value={(v as string) ?? ''}
          placeholder="—"
          options={[{ value: '', label: '—' }, ...opcoes]}
          onChange={(val) => aplicar(val || null)}
        />
      )
    }
    case 'multiselect': {
      const sel = Array.isArray(v) ? (v as string[]) : []
      const opcoes = f.config.options ?? []
      if (layoutDaEscolha(f.config) === 'tag') {
        return (
          <TagSelect
            value={sel}
            options={opcoes}
            emptyLabel="Nenhuma opção cadastrada"
            placeholder="Escolha…"
            onChange={(vals) => aplicar(vals)}
          />
        )
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {opcoes.map((o) => (
            <Checkbox
              key={o.value}
              checked={sel.includes(o.value)}
              label={o.label}
              onChange={(e) =>
                aplicar(e.target.checked ? [...sel, o.value] : sel.filter((x) => x !== o.value))
              }
            />
          ))}
        </div>
      )
    }
    default: {
      // texto curto: a máscara do campo formata enquanto digita (CPF, CEP, …)
      const mascara = mascaraDoCampo(f.config)
      const formata = (texto: string) => aplicarMascara(texto, mascara)
      return (
        <Input
          value={formata((v as string) ?? '')}
          placeholder={mascara?.exemplo || undefined}
          inputMode={mascara?.inputMode}
          onChange={(e) => setValores((s) => ({ ...s, [f.key]: formata(e.target.value) }))}
          onBlur={(e) => gravar(f.key, formata(e.target.value))}
        />
      )
    }
  }
}

/** Campos de um grupo (ou barra, gid=null) pelas colocações, deduplicado por chave, com largura e a colocação. */
export function camposDoGrupo(campos: CustomField[], placements: CustomFieldPlacement[], gid: string | null) {
  const porId = new Map(campos.map((f) => [f.id, f]))
  const vistos = new Set<string>()
  const out: { campo: CustomField; width: number; placement: CustomFieldPlacement }[] = []
  for (const p of placements.filter((x) => x.groupId === gid).sort((a, b) => a.sortOrder - b.sortOrder)) {
    const f = porId.get(p.fieldId)
    if (f && !vistos.has(f.key)) {
      vistos.add(f.key)
      out.push({ campo: f, width: Math.min(12, Math.max(1, p.width || 12)), placement: p })
    }
  }
  return out
}
